export interface TasklistHeroTask {
  id: number;
  /** Backend `/tasks` id when it differs from tasklist row `id` */
  taskId?: number;
  /**
   * When CMS `/subtasks?taskId=` parent id does not match the static JSON route (`pageId`),
   * set this to the onboarding v2 parent id whose `taskTitle` rows align with `public/data/pages/{pageId}.json` subtasks.
   * `PUT /tasks/complete` still uses {@link taskId} or numeric {@link pageId} via {@link resolveLearningCenterBackendTaskId}.
   */
  subtasksCmsParentId?: number;
  title: string;
  /** Stable key from CMS / API (unchanged in JSON payloads) */
  checklistName: string;
  completed?: boolean;
  pathSegment?: string;
  pageId?: string;
  group?: string;
  roles?: string[];
  /** When set, completion applies only to this account */
  accountId?: number | string;
}

export interface TasklistLearningCenterTask {
  id: number;
  /** Backend `/tasks` id (often matches `pageId`); used for autocomplete merge and PUTs */
  taskId?: number;
  title: string;
  description: string;
  taskType: 'anytime' | 'actionable';
  /** Ignored when API completion is available; kept for offline / dev fallback only */
  completed?: boolean;
  order: number;
  pageId?: string;
  pathSegment?: string;
  /** Optional grouping for CMS / analytics (not used for completion) */
  group?: string;
  roles?: string[];
}

export interface TasklistJsonRoot {
  tasks: TasklistHeroTask[];
  learningCenterTasks?: TasklistLearningCenterTask[];
}

/**
 * Supports legacy `{ tasks, learningCenterTasks }` or a single array of mixed rows
 * (hero tasks with `checklistName`, Learning Center rows with `taskType`).
 */
export function normalizeTasklistJsonPayload(data: unknown): {
  tasks: TasklistHeroTask[];
  learningCenterTasks: TasklistLearningCenterTask[];
} {
  if (Array.isArray(data)) {
    const tasks: TasklistHeroTask[] = [];
    const learningCenterTasks: TasklistLearningCenterTask[] = [];
    for (const raw of data) {
      if (!raw || typeof raw !== 'object') continue;
      const o = raw as Record<string, unknown>;
      const taskType = o.taskType;
      const isLc =
        (taskType === 'anytime' || taskType === 'actionable') &&
        typeof o.description === 'string';
      if (isLc) {
        learningCenterTasks.push(raw as TasklistLearningCenterTask);
      } else if (typeof o.checklistName === 'string') {
        tasks.push(raw as TasklistHeroTask);
      }
    }
    return { tasks, learningCenterTasks };
  }

  if (data && typeof data === 'object' && !Array.isArray(data)) {
    const f = data as TasklistJsonRoot;
    return {
      tasks: Array.isArray(f.tasks) ? f.tasks : [],
      learningCenterTasks: f.learningCenterTasks ?? [],
    };
  }

  return { tasks: [], learningCenterTasks: [] };
}
